// v101.246.0 · Barra de avance del cierre a Odoo: muestra en vivo cada proceso que corre por detrás
// (consulta cada segundo GET /v1/ot_smart/{id}/close-progress mientras el cierre está en curso).
import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Circle, XCircle, Clock } from "lucide-react";
import api from "../lib/api";

const PASOS_BASE = [
  { clave: "inicio", nombre: "Revisando la OT (aprovisionamiento y motivo de cierre)" },
  { clave: "odoo_estado", nombre: "Consultando en Odoo si la tarea ya está cerrada" },
  { clave: "reglas", nombre: "Revisando tarea padre y reglas de validación" },
  { clave: "materiales", nombre: "Preparando materiales y receta" },
  { clave: "bodega", nombre: "Verificando los equipos en la bodega del técnico (Odoo)" },
  { clave: "red", nombre: "Armando datos técnicos (caja, puerto, VLAN, WiFi)" },
  { clave: "devoluciones", nombre: "Preparando equipos devueltos" },
  { clave: "consulta", nombre: "Consulta previa a Odoo" },
  { clave: "envio", nombre: "Enviando el cierre a Odoo (espera hasta 40 s)" },
  { clave: "final", nombre: "Respuesta de Odoo" },
];

export default function CloseProgressModal({ odooTaskId, open }) {
  const [prog, setProg] = useState(null);
  const [seg, setSeg] = useState(0);
  const t0 = useRef(0);

  useEffect(() => {
    if (!open || !odooTaskId) { setProg(null); setSeg(0); return undefined; }
    t0.current = Date.now();
    let vivo = true;
    const leer = async () => {
      try {
        const { data } = await api.get(`/v1/ot_smart/${odooTaskId}/close-progress`, { timeout: 5000 });
        if (vivo) setProg(data);
      } catch (_) { /* sin progreso: se sigue mostrando el último */ }
    };
    leer();
    const a = setInterval(leer, 1000);
    const b = setInterval(() => setSeg(Math.floor((Date.now() - t0.current) / 1000)), 500);
    return () => { vivo = false; clearInterval(a); clearInterval(b); };
  }, [open, odooTaskId]);

  if (!open) return null;
  // Mientras no llega el primer dato (o es de un cierre anterior), se muestra el primer paso en curso
  const reciente = prog && prog.actual && (prog.segundos == null || prog.segundos <= seg + 5);
  const pasos = (reciente ? prog.pasos : PASOS_BASE.map((p, i) => ({ ...p, estado: i === 0 ? "en_curso" : "pendiente" })))
    .filter((p) => p.clave !== "devoluciones" || p.estado !== "pendiente" || (reciente && prog.actual === "devoluciones"));
  const pct = reciente ? Math.max(3, Math.min(100, prog.pct || 0)) : 3;
  const actual = pasos.find((p) => p.estado === "en_curso" || p.estado === "error");
  const lento = actual && actual.clave === "envio" && seg > 15;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" data-testid="close-progress">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-bold text-slate-800">Cerrando la OT en Odoo</h3>
          <span className="flex items-center gap-1 text-xs text-slate-500"><Clock size={13} /> {seg} s</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          No cierre esta ventana ni vuelva a pulsar «Validar/Cerrar»: el sistema está haciendo estos pasos.
        </p>
        <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden mb-4">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <ul className="space-y-1.5">
          {pasos.map((p) => (
            <li key={p.clave} className="flex items-start gap-2 text-sm">
              {p.estado === "hecho" || p.estado === "ok" ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                : p.estado === "en_curso" ? <Loader2 size={16} className="animate-spin text-sky-600 mt-0.5 shrink-0" />
                  : p.estado === "error" ? <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                    : p.estado === "pendiente" && p.clave === "final" && reciente && prog.estado === "pendiente"
                      ? <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />
                      : <Circle size={16} className="text-slate-300 mt-0.5 shrink-0" />}
              <span className={p.estado === "en_curso" ? "font-semibold text-slate-800"
                : p.estado === "pendiente" ? "text-slate-400" : "text-slate-600"}>{p.nombre}</span>
            </li>
          ))}
        </ul>
        {reciente && prog.detalle && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">{prog.detalle}</div>
        )}
        {lento && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            Odoo está procesando el cierre (descarga de materiales y cierre de la tarea). Puede tardar hasta 40 s.
          </div>
        )}
      </div>
    </div>
  );
}
